import { useState, useEffect } from "react";
import { useRouter } from "next/router";
import _ from "lodash";
import AddToCartModal from "@/components/modal/AddToCartModal";
import SortModal from "@/components/categories/modal/SortModal";
import Breadcrumbs from "@/components/breadcrumbs";
import CustomSelect from "@/components/form/select/CustomSelect";
import Pagination from "@/components/pagination";
import CartSidebarWrapper from "@/components/cart/sidebarWrapper";
import Card from "@/components/product-card/large/Card";
import Loader from "@/components/loader";
import NotFound from "@/components/error/NotFound";
import en from "@/localization/en.json";
import { useFetchCategoryData } from "repo/category";
import { fetchCategoryInfo } from "@/api/category";
import { useFetchSortOptions } from "repo/search";
import { useFetchWishListInfo } from "repo/wishlist";
import { getWishlistUpdatedProducts } from "@/utils/helpers/wishlist";
import FilterByBtn from "@/components/actions/button/FilterBy";
import SortByBtn from "@/components/actions/button/SortBy";
import { useSession } from "next-auth/react";
import {
  FilterMobileView,
  FourOFour,
  ProductFilters,
} from "@/components/index";
import { usePagination } from "hooks/usePagination";
import { useSort } from "hooks/useSort";

function FeaturedProductsPage({ featuredData }) {
  const [searchFeaturedDataInfo, setSearchFeaturedDataInfo] =
    useState(featuredData);
  const [rowData, setRowData] = useState(featuredData?.attributes || []);
  const [featuredProducts, setFeaturedProducts] = useState(
    featuredData?.products
  );
  const router = useRouter();
  const { data: session } = useSession();
  const { getPaginateData } = usePagination();

  const { isFetching: isFeaturedCategoryFetching } = useFetchCategoryData(
    router.asPath,
    {
      onSuccess: (featuredData) => {
        setSearchFeaturedDataInfo(featuredData);
        setFeaturedProducts(featuredData.products);
        setRowData(featuredData.attributes);
      },
      enabled: !!Object.keys(router.query).length,
    }
  );

  const { data: sortOptionData = [] } = useFetchSortOptions();
  const { sortData, setSelectedSortValue } = useSort(sortOptionData);

  const { refetch: refetchWishListData } = useFetchWishListInfo({
    enabled: false,
    onSuccess: (wishlistData) => {
      if (!wishlistData.length) return;
      if (!_.isEmpty(featuredProducts)) {
        setFeaturedProducts(
          getWishlistUpdatedProducts(wishlistData, featuredProducts)
        );
      }
    },
  });

  useEffect(() => {
    if (session?.token) {
      refetchWishListData();
    }
  }, [session]);

  const calculateSearchResult = (resultData) => {
    if (resultData.total === 0) {
      return 0;
    } else {
      return `${resultData.from} - ${
        resultData.total > resultData.per_page
          ? resultData.to
          : resultData.total
      } of ${resultData.total}`;
    }
  };

  if (!searchFeaturedDataInfo) {
    return <FourOFour />;
  }

  return (
    <>
      {isFeaturedCategoryFetching && <Loader />}
      <div className="w-full sm:flex pb-[1rem]">
        <div className="hidden w-1/4 sm:flex flex-col items-center">
          <div className="sm:flex flex-col items-start">
            <p className="text-grayD4 text-heading4 font-normal mt-[2rem]">
              {en["common.filter_by"]}
            </p>

            <div>
              <ProductFilters
                filterData={rowData}
                prices={searchFeaturedDataInfo.prices}
              />
            </div>
          </div>
        </div>

        <div className="w-full sm:w-3/4 sm:mr-24 mt-[1.375rem]">
          <div className="pl-[1.25rem] sm:pl-0">
            <Breadcrumbs
              breadcrumbs={[
                { value: "Featured Product", link: "/featured-product" },
              ]}
            />
          </div>
          <div className="sm:flex justify-between mt-[1.875rem] pl-[1.25rem] sm:pl-0 sm:pb-3">
            <div className="space-x-[0.75rem] sm:space-x-0  sm:space-y-[0.625rem] flex items-center sm:block">
              <p className="text-[#0A101A] text-[1.5rem] font-semibold">
                Featured Product
              </p>

              {searchFeaturedDataInfo?.meta && (
                <p className="text-[#414E5F] text-[0.75rem]">
                  {calculateSearchResult(searchFeaturedDataInfo.meta)}&nbsp;
                  {en["common.results"]}
                </p>
              )}
            </div>

            <div className="hidden sm:flex items-center">
              <label className="text-[#7B838F]">{en["common.sort_by"]}</label>
              <CustomSelect
                options={sortOptionData}
                onChange={(state) => sortData(state)}
                value={setSelectedSortValue}
              />
            </div>
          </div>

          {/* start mobile screen sort filter options */}
          <div className="pb-[2rem] pt-[3rem] sm:hidden">
            <div className="flex items-center justify-center space-x-[1rem]">
              <FilterByBtn />
              <SortByBtn />
            </div>
            <FilterMobileView
              filterData={rowData}
              prices={searchFeaturedDataInfo.price}
            />
            <SortModal
              sortData={sortOptionData}
              onChange={(data) => sortData({ value: data })}
              value={setSelectedSortValue}
            />
          </div>

          {/* end mobile screen sort filter options */}

          {featuredProducts && featuredProducts.length > 0 ? (
            <div className="w-full mx-auto grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-4">
              {featuredProducts.map((product) => (
                <Card product={product} key={product.slug} />
              ))}
            </div>
          ) : (
            <NotFound
              imageHolderClassName="w-[15rem] md:w-[20rem] h-[11.25rem] md:h-[15rem]"
              imageName="no-product-found.png"
              wrapperClassName="mx-auto mt-[4.375rem] mb-[8.43rem]"
              heading=""
              bodyText={en["common.no_product_found"]}
              bodyClassName="mt-[1.25rem] text-grayD3 text-body1 md:text-heading5 max-w-[23.5rem]"
            />
          )}
        </div>
      </div>
      <CartSidebarWrapper />
      <AddToCartModal />
      {searchFeaturedDataInfo?.meta?.total >
        searchFeaturedDataInfo?.meta?.per_page && (
        <div className="mb-[5.625rem]">
          <Pagination
            total={Math.ceil(
              searchFeaturedDataInfo?.meta?.total /
                searchFeaturedDataInfo?.meta?.per_page
            )}
            currentPage={searchFeaturedDataInfo?.meta?.current_page - 1}
            getPaginateData={(data) => getPaginateData(data)}
          />
        </div>
      )}
    </>
  );
}

export async function getStaticProps() {
  const data = await fetchCategoryInfo(`/featured-products`);
  if (data) {
    return {
      props: {
        featuredData: data.data,
      },
    };
  }

  return {
    props: { offersData: null },
  };
}

export default FeaturedProductsPage;
